/**
 * useDeleteServiceAccount Hook
 *
 * React Query mutation hook for deleting a service account.
 */

import { useMutation, useQueryClient } from '@tanstack/react-query';
import type { UseMutationResult } from '@tanstack/react-query';
import { serviceAccountService } from '../../../api/service-account.service';
import { SERVICE_ACCOUNTS_QUERY_KEY } from './useServiceAccounts';
import { SERVICE_ACCOUNT_QUERY_KEY } from './useServiceAccount';
import { getErrorMessage } from '../../../lib/utils/errors';

interface UseDeleteServiceAccountOptions {
  onSuccess?: () => void;
  onError?: (error: string) => void;
}

/**
 * Hook for deleting a service account
 * @param tenantId - The tenant ID
 * @param options - Callbacks for success and error
 * @returns Mutation that accepts a serviceAccountId
 */
export const useDeleteServiceAccount = (
  tenantId: string,
  options?: UseDeleteServiceAccountOptions
): UseMutationResult<void, Error, string> => {
  const queryClient = useQueryClient();

  return useMutation<void, Error, string>({
    mutationFn: (serviceAccountId) => serviceAccountService.deleteServiceAccount(tenantId, serviceAccountId),
    onSuccess: (_, serviceAccountId) => {
      // Remove from cache
      queryClient.removeQueries({ queryKey: [SERVICE_ACCOUNT_QUERY_KEY, tenantId, serviceAccountId] });
      // Invalidate service accounts list to refresh the data
      queryClient.invalidateQueries({ queryKey: [SERVICE_ACCOUNTS_QUERY_KEY, tenantId] });
      options?.onSuccess?.();
    },
    onError: (error) => {
      options?.onError?.(getErrorMessage(error));
    },
  });
};
